import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import AxiosInstance, { setupInterceptors } from "../utils/AxiosInstance";
import { useShowAlert } from "../context/AlertContext";
import Messages from "../constants/Messages";
import uRLs from "../utils/baseUtils";
import axios from "axios";
import SubscriptionExpiredModal from "../constants/SubscriptionExpiredModal";

const AuthContext = createContext();

const Domain = window.location.hostname;
var cookieName = uRLs.COOKIE_NAME;
if (Domain === "" + uRLs.DOMAIN_ONE + "") {
  cookieName = uRLs.COOKIE_NAME;
} else if (Domain === "" + uRLs.DOMAIN_TWO + "") {
  cookieName = uRLs.COOKIE_NAME_ONE;
}

export const AuthProvider = ({ children }) => {
  const [isUser, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [isStoreExpired, setIsStoreExpired] = useState(false);
  const showAlert = useShowAlert();
  const navigate = useNavigate();

  const updateSettings = (newSettings) => {
    setSettings((prevSettings) => {
      let updatedCustomerGroups = Array.isArray(prevSettings?.customerGroups)
        ? prevSettings?.customerGroups
        : [];

      if (Array.isArray(newSettings?.customerGroups)) {
        updatedCustomerGroups = updatedCustomerGroups.map((g) => {
          const updated = newSettings?.customerGroups.find(
            (newG) => newG?._id === g?._id,
          );
          return updated ? { ...g, ...updated } : g; // update if match, else keep
        });

        // Add truly new groups that don't exist in prev
        const newGroups = newSettings?.customerGroups?.filter(
          (newG) => !updatedCustomerGroups?.some((g) => g?._id === newG?._id),
        );

        updatedCustomerGroups = [...newGroups, ...updatedCustomerGroups];
      }

      return {
        ...prevSettings,
        customerGroups: updatedCustomerGroups,
        storeSettings: {
          ...prevSettings.storeSettings,
          ...newSettings.storeSettings,
          saleSettings: {
            ...prevSettings.storeSettings?.saleSettings,
            ...newSettings.storeSettings?.saleSettings,
            saleModeSettings: {
              ...prevSettings.storeSettings?.saleSettings?.saleModeSettings,
              ...newSettings.storeSettings?.saleSettings?.saleModeSettings,
            },
          },
        },
      };
    });
  };

  const handleGetToken = useCallback(() => {
    try {
      const cookieData = Cookies.get(cookieName);
      return cookieData ? JSON.parse(cookieData) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const handleUpdateToken = useCallback((newAccessToken) => {
    try {
      const cookieData = Cookies.get(cookieName);
      if (!cookieData) return null;
      const parsedData = JSON.parse(cookieData);
      parsedData.accessStoreToken = newAccessToken;
      Cookies.set(cookieName, JSON.stringify(parsedData), {
        secure: true,
        sameSite: "Lax",
        path: "/",
        expires: 300,
      });
      return newAccessToken;
    } catch (error) {
      return null;
    }
  }, []);

  const handleStoreSetup = async (accessToken) => {
    try {
      const storeSetting = await AxiosInstance.get("/module/store/setup", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setSettings(storeSetting.data.results);

      return true;
    } catch (error) {
      if (
        error.response?.status === 403 &&
        error.response?.data?.code === "STORE_EXPIRED"
      ) {
        setIsStoreExpired(true);
        return false;
      }

      throw error;
    }
  };

  const handleUser = useCallback(async () => {
    // setLoading(true);
    try {
      const tokenData = handleGetToken();
      if (!tokenData) {
        return false;
      }
      const response = await AxiosInstance.get("/auth-token", {
        headers: { Authorization: `Bearer ${tokenData.accessStoreToken}` },
      });
      // const profileRes = await AxiosInstance.get("/module/storeprofile/view-profile-details", {
      //     headers: { Authorization: `Bearer ${tokenData.accessStoreToken}` },
      // });

      //   const storeSetting = await AxiosInstance.get("/module/store/setup", {
      //     headers: { Authorization: `Bearer ${tokenData.accessStoreToken}` },
      //   });

      const combinedData = {
        ...response.data.results,
        // profileData: profileRes.data.results
      };

      setUser(combinedData);
      await handleStoreSetup(tokenData.accessStoreToken);
    } catch (error) {
      if (
        error.response?.status === 401 &&
        error.response?.data?.code === "TOKEN_EXPIRED"
      ) {
        try {
          const tokenData = handleGetToken();
          if (!tokenData?.refreshStoreToken) {
            throw new Error("No refresh token available");
          }
          const response = await axios.get(`${uRLs.API_URL}create-token`, {
            headers: {
              Authorization: `Bearer ${tokenData.refreshStoreToken}`,
            },
          });
          const newAccessToken = response.data.results.accessToken;
          handleUpdateToken(newAccessToken);

          const retryResponse = await AxiosInstance.get("/auth-token", {
            headers: { Authorization: `Bearer ${newAccessToken}` },
          });

          // const profileRes = await AxiosInstance.get("/module/storeprofile/view-profile-details", {
          //     headers: { Authorization: `Bearer ${tokenData.accessStoreToken}` },
          // });

          //   const storeSetting = await AxiosInstance.get("/module/store/setup", {
          //     headers: { Authorization: `Bearer ${tokenData.accessStoreToken}` },
          //   });

          const combinedData = {
            ...retryResponse.data.results,
            // profileData: profileRes.data.results,
          };

          setUser(combinedData);
          //   setSettings(storeSetting.data.results);
          await handleStoreSetup(newAccessToken);
        } catch (refreshError) {
          handleLogout();
        }
      } else {
        showAlert("warning", error.message || Messages.ERROR.SERVER_ERROR);
      }
    } finally {
      setLoading(false);
    }
  }, [handleGetToken]);

  useEffect(() => {
    handleUser();
  }, [handleUser]);

  const handleLogin = useCallback(
    (tokens) => {
      showAlert("success", Messages.AUTH.LOGIN_SUCCESS);
      const tokenData = {
        accessStoreToken: tokens.accessToken,
        refreshStoreToken: tokens.refreshToken,
      };
      Cookies.set(cookieName, JSON.stringify(tokenData), {
        secure: true,
        sameSite: "Lax",
        path: "/",
        expires: 300,
      });
      handleUser();
    },
    [showAlert, handleUser],
  );

  const handleLogout = useCallback(() => {
    showAlert("warning", "Your session has expired. Please log in again.");
    Cookies.remove(cookieName);
    setUser(null);
    navigate("/login", { replace: true });
  }, [showAlert, navigate]);

  const handleResetLogin = useCallback(() => {
    Cookies.remove(cookieName);
    setUser(null);
  }, [showAlert, navigate]);

  useEffect(() => {
    setupInterceptors({ handleGetToken, handleUpdateToken, handleLogout });
  }, [handleGetToken, handleUpdateToken, handleLogout, handleResetLogin]);

  useEffect(() => {
    setStartTime(Date.now()); // records current time in milliseconds
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isUser,
        isLoading,
        settings,
        startTime,
        setSettings,
        updateSettings,
        handleLogin,
        handleGetToken,
        handleUpdateToken,
        handleLogout,
        handleUser,
        handleResetLogin,
      }}
    >
      {children}
      <SubscriptionExpiredModal isOpen={isStoreExpired} />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
